import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, X, GripVertical, Settings2, Trash2, ChevronDown, ChevronRight, FilePlus, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { key: 'checkbox',          label: 'Checkbox',                hint: 'Tech ticks done / not done' },
  { key: 'yes_no',            label: 'Yes / No',                hint: 'Two-button toggle' },
  { key: 'text',              label: 'Short Text',              hint: 'Single-line input' },
  { key: 'textarea',          label: 'Long Text',               hint: 'Multi-line note' },
  { key: 'select',            label: 'Dropdown',                hint: 'Pick one of N options' },
  { key: 'equipment_block',   label: 'Equipment Survey',        hint: 'Renders the asset-capture UI' },
  { key: 'opportunity_flag',  label: 'Opportunity Flag',        hint: 'Feeds the opportunity report' },
];

const OPPORTUNITY_KEYS = [
  'water_heater_old', 'no_battery_backup', 'membership_prospect',
  'softener_replacement', 'tankless_upgrade', 'financing_prospect',
  'repipe_opportunity', 'sewer_replacement',
];

const FormBuilder = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tmpl, setTmpl] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/completion-templates?include_inactive=true`, { withCredentials: true });
      setTemplates(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  }, [selectedId]);

  const loadTmpl = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await axios.get(`${API}/admin/completion-templates/${selectedId}`, { withCredentials: true });
      setTmpl(data); setDirty(false);
    } catch { /* */ }
  }, [selectedId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadTmpl(); }, [loadTmpl]);

  const newTemplate = async () => {
    const name = prompt('Name for the new completion form:');
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/admin/completion-templates`, {
        name, is_default: false, is_active: true, categories: [],
      }, { withCredentials: true });
      toast.success('Template created');
      setSelectedId(data.id);
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveTmpl = async () => {
    if (!tmpl) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/completion-templates/${tmpl.id}`, {
        name: tmpl.name,
        is_default: !!tmpl.is_default,
        is_active: tmpl.is_active !== false,
        departments: tmpl.departments || [],
        service_types: tmpl.service_types || [],
        categories: tmpl.categories || [],
      }, { withCredentials: true });
      toast.success('Saved');
      setDirty(false);
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteTmpl = async () => {
    if (!tmpl) return;
    if (tmpl.is_default) { toast.error('Cannot delete the default template'); return; }
    if (!window.confirm(`Delete "${tmpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/completion-templates/${tmpl.id}`, { withCredentials: true });
      toast.success('Deleted'); setSelectedId(null); setTmpl(null); loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const mutate = (mutator) => {
    setTmpl((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      mutator(next);
      // Re-number order indexes
      (next.categories || []).forEach((c, i) => {
        c.order = i;
        (c.questions || []).forEach((q, j) => { q.order = j; });
      });
      return next;
    });
    setDirty(true);
  };

  const addCategory = () => mutate((t) => {
    t.categories = t.categories || [];
    t.categories.push({ name: 'New Category', order: t.categories.length, questions: [] });
  });
  const renameCategory = (idx, name) => mutate((t) => { t.categories[idx].name = name; });
  const deleteCategory = (idx) => mutate((t) => { t.categories.splice(idx, 1); });
  const moveCategory = (idx, dir) => mutate((t) => {
    const target = idx + dir;
    if (target < 0 || target >= t.categories.length) return;
    [t.categories[idx], t.categories[target]] = [t.categories[target], t.categories[idx]];
  });

  const addQuestion = (cIdx, type = 'checkbox') => mutate((t) => {
    t.categories[cIdx].questions = t.categories[cIdx].questions || [];
    t.categories[cIdx].questions.push({
      label: 'New question', type, required: false, options: [], opportunity_key: type === 'opportunity_flag' ? OPPORTUNITY_KEYS[0] : null,
    });
  });
  const patchQuestion = (cIdx, qIdx, patch) => mutate((t) => { Object.assign(t.categories[cIdx].questions[qIdx], patch); });
  const deleteQuestion = (cIdx, qIdx) => mutate((t) => { t.categories[cIdx].questions.splice(qIdx, 1); });
  const moveQuestion = (cIdx, qIdx, dir) => mutate((t) => {
    const arr = t.categories[cIdx].questions;
    const target = qIdx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[qIdx], arr[target]] = [arr[target], arr[qIdx]];
  });

  return (
    <div data-testid="form-builder-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Completion Form Builder</h2>
          <p className="text-[#A3A3A3]">Build the checklists, equipment surveys, and opportunity flags your techs see when they wrap a job. No developer required.</p>
        </div>
        <Button data-testid="form-builder-new" onClick={newTemplate} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
          <FilePlus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar: template list */}
        <div className="col-span-12 lg:col-span-3 space-y-2">
          {templates.map((t) => (
            <button
              key={t.id}
              data-testid={`form-builder-tmpl-${t.id}`}
              onClick={() => setSelectedId(t.id)}
              className={`w-full text-left bg-[#141414] border rounded-md px-3 py-2 hover:border-[#D4AF37] transition-colors ${selectedId === t.id ? 'border-[#D4AF37]' : 'border-[#262626]'}`}
            >
              <div className="flex items-center gap-2">
                {t.is_default && <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />}
                <span className="text-white text-sm font-medium truncate">{t.name}</span>
              </div>
              <div className="text-[10px] text-[#A3A3A3] mt-0.5">
                {t.is_active === false ? <span className="text-red-400">Inactive</span> : `${(t.categories || []).length} categories`}
                {t.service_types?.length > 0 && <span className="ml-2">• {t.service_types.join(', ')}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="col-span-12 lg:col-span-9 space-y-4">
          {tmpl ? (
            <>
              <div className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Template Name</Label>
                    <Input data-testid="fb-name" value={tmpl.name || ''}
                      onChange={(e) => { setTmpl({ ...tmpl, name: e.target.value }); setDirty(true); }}
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Target Service Types (comma-sep)</Label>
                    <Input data-testid="fb-services" value={(tmpl.service_types || []).join(', ')}
                      onChange={(e) => { setTmpl({ ...tmpl, service_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }); setDirty(true); }}
                      placeholder="e.g. Drain Cleaning, Water Heater Install"
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-sm text-white">
                    <input data-testid="fb-default" type="checkbox" checked={!!tmpl.is_default}
                      onChange={(e) => { setTmpl({ ...tmpl, is_default: e.target.checked }); setDirty(true); }}
                      className="accent-[#D4AF37]" /> Default template
                  </label>
                  <label className="flex items-center gap-2 text-sm text-white">
                    <input data-testid="fb-active" type="checkbox" checked={tmpl.is_active !== false}
                      onChange={(e) => { setTmpl({ ...tmpl, is_active: e.target.checked }); setDirty(true); }}
                      className="accent-[#D4AF37]" /> Active (shown to techs)
                  </label>
                  <Button data-testid="fb-add-category" onClick={addCategory} variant="outline" className="border-[#262626] text-white">
                    <Plus className="w-4 h-4 mr-1" /> Add Category
                  </Button>
                  <div className="flex-1" />
                  <Button data-testid="fb-save" onClick={saveTmpl} disabled={!dirty || saving}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-40">
                    {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
                  </Button>
                  {!tmpl.is_default && (
                    <Button data-testid="fb-delete" onClick={deleteTmpl} variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {(tmpl.categories || []).map((cat, cIdx) => {
                const isCollapsed = !!collapsed[cat.id || cIdx];
                return (
                  <div key={cat.id || cIdx} data-testid={`fb-cat-${cIdx}`} className="bg-[#141414] border border-[#262626] rounded-md">
                    <div className="flex items-center gap-2 p-3 border-b border-[#262626]">
                      <button onClick={() => setCollapsed({ ...collapsed, [cat.id || cIdx]: !isCollapsed })} className="text-[#A3A3A3] hover:text-white">
                        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <Input
                        value={cat.name}
                        onChange={(e) => renameCategory(cIdx, e.target.value)}
                        className="bg-transparent border-none text-white font-semibold flex-1 px-1 focus-visible:ring-0"
                        data-testid={`fb-cat-name-${cIdx}`}
                      />
                      <span className="text-[#A3A3A3] text-xs">{(cat.questions || []).length} questions</span>
                      <Button size="sm" variant="ghost" onClick={() => moveCategory(cIdx, -1)} className="text-[#A3A3A3] h-7 w-7 p-0">▲</Button>
                      <Button size="sm" variant="ghost" onClick={() => moveCategory(cIdx, 1)} className="text-[#A3A3A3] h-7 w-7 p-0">▼</Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCategory(cIdx)} className="text-red-400 hover:bg-red-900/20 h-7 w-7 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {!isCollapsed && (
                      <div className="p-3 space-y-2">
                        {(cat.questions || []).map((q, qIdx) => (
                          <div key={qIdx} data-testid={`fb-q-${cIdx}-${qIdx}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                            <div className="flex items-start gap-2">
                              <GripVertical className="w-4 h-4 text-[#525252] mt-2 shrink-0" />
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_180px_120px] gap-2">
                                <Input
                                  value={q.label}
                                  onChange={(e) => patchQuestion(cIdx, qIdx, { label: e.target.value })}
                                  placeholder="Question label"
                                  className="bg-[#141414] border-[#262626] text-white"
                                />
                                <Select value={q.type} onValueChange={(v) => patchQuestion(cIdx, qIdx, { type: v, opportunity_key: v === 'opportunity_flag' && !q.opportunity_key ? OPPORTUNITY_KEYS[0] : q.opportunity_key })}>
                                  <SelectTrigger className="bg-[#141414] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                                  <SelectContent className="bg-[#141414] border-[#262626]">
                                    {QUESTION_TYPES.map((t) => <SelectItem key={t.key} value={t.key} className="text-white">{t.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <label className="flex items-center justify-center gap-2 text-xs text-white border border-[#262626] rounded-md bg-[#141414] cursor-pointer">
                                  <input type="checkbox" checked={!!q.required}
                                    onChange={(e) => patchQuestion(cIdx, qIdx, { required: e.target.checked })}
                                    className="accent-[#D4AF37]" /> Required
                                </label>
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => moveQuestion(cIdx, qIdx, -1)} className="text-[#A3A3A3] h-7 w-7 p-0">▲</Button>
                              <Button size="sm" variant="ghost" onClick={() => moveQuestion(cIdx, qIdx, 1)} className="text-[#A3A3A3] h-7 w-7 p-0">▼</Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteQuestion(cIdx, qIdx)} className="text-red-400 hover:bg-red-900/20 h-7 w-7 p-0">
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {q.type === 'select' && (
                              <Textarea
                                value={(q.options || []).join('\n')}
                                onChange={(e) => patchQuestion(cIdx, qIdx, { options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                                placeholder="One option per line"
                                rows={3}
                                className="bg-[#141414] border-[#262626] text-white"
                              />
                            )}
                            {q.type === 'opportunity_flag' && (
                              <Select value={q.opportunity_key || OPPORTUNITY_KEYS[0]} onValueChange={(v) => patchQuestion(cIdx, qIdx, { opportunity_key: v })}>
                                <SelectTrigger className="bg-[#141414] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#141414] border-[#262626]">
                                  {OPPORTUNITY_KEYS.map((k) => <SelectItem key={k} value={k} className="text-white">{k.replace(/_/g, ' ')}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            {q.help_text !== undefined && (
                              <Input
                                value={q.help_text || ''}
                                onChange={(e) => patchQuestion(cIdx, qIdx, { help_text: e.target.value })}
                                placeholder="Help text for tech (optional)"
                                className="bg-[#141414] border-[#262626] text-white text-xs"
                              />
                            )}
                          </div>
                        ))}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {['checkbox', 'yes_no', 'text', 'textarea', 'opportunity_flag', 'equipment_block'].map((t) => (
                            <Button key={t} size="sm" variant="outline"
                              onClick={() => addQuestion(cIdx, t)}
                              className="border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#D4AF37] text-xs">
                              <Plus className="w-3 h-3 mr-1" /> {QUESTION_TYPES.find((x) => x.key === t)?.label || t}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {(tmpl.categories || []).length === 0 && (
                <div className="bg-[#141414] border border-[#262626] rounded-md p-10 text-center">
                  <Settings2 className="w-8 h-8 text-[#525252] mx-auto mb-2" />
                  <p className="text-white">No categories yet. Click <span className="text-[#D4AF37]">Add Category</span> to start.</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-[#141414] border border-[#262626] rounded-md p-10 text-center text-[#A3A3A3]">
              Select a template on the left, or create one with <span className="text-[#D4AF37]">New Template</span>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
